import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../utils/type';
import {
  selectVendorCommissions,
  selectVendorCommissionLoading,
  selectVendorCommissionError,
  selectVendorCommissionUpdating,
  selectVendorCommissionUpdateError,
  selectVendorCommissionUpdateSuccess,
} from '../services/vendorCommission/selectors';
import {
  getVendorCommissions,
  setVendorCommission,
} from '../services/vendorCommission/crudFunctions';
import { clearUpdateStatus } from '../services/vendorCommission/slice';
import { VendorCommission } from '../model/commission';
import LoadingSpinner from '../../../elements/LoadingSpinner';
import Pagination from '../../../elements/Pagination';

const PAGE_SIZE = 10;

const VendorCommissionPage: React.FC = () => {
  const dispatch = useDispatch();
  const data = useSelector((s: RootState) => selectVendorCommissions(s));
  const loading = useSelector((s: RootState) => selectVendorCommissionLoading(s));
  const error = useSelector((s: RootState) => selectVendorCommissionError(s));
  const updating = useSelector((s: RootState) => selectVendorCommissionUpdating(s));
  const updateError = useSelector((s: RootState) => selectVendorCommissionUpdateError(s));
  const updateSuccess = useSelector((s: RootState) => selectVendorCommissionUpdateSuccess(s));

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<VendorCommission | null>(null);
  const [newPercentage, setNewPercentage] = useState('');
  const [percentageErr, setPercentageErr] = useState('');

  useEffect(() => {
    dispatch<any>(getVendorCommissions());
  }, [dispatch]);

  // Auto-close modal on success
  useEffect(() => {
    if (updateSuccess) {
      setModalOpen(false);
      setSelected(null);
      setNewPercentage('');
      // Clear success message after 3s
      const t = setTimeout(() => dispatch(clearUpdateStatus()), 3000);
      return () => clearTimeout(t);
    }
  }, [updateSuccess, dispatch]);

  const openModal = (vendor: VendorCommission) => {
    setSelected(vendor);
    setNewPercentage(String(vendor.commission_percentage));
    setPercentageErr('');
    dispatch(clearUpdateStatus());
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
    setNewPercentage('');
    setPercentageErr('');
    dispatch(clearUpdateStatus());
  };

  const handleUpdate = () => {
    const val = Number(newPercentage);
    if (newPercentage === '' || isNaN(val)) {
      setPercentageErr('Please enter a valid percentage.');
      return;
    }
    if (val < 0 || val > 100) {
      setPercentageErr('Percentage must be between 0 and 100.');
      return;
    }
    setPercentageErr('');
    if (selected) {
      dispatch<any>(setVendorCommission(selected.vendor_id, val));
    }
  };

  // Client-side search
  const filtered = data.filter((v) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (v.vendor_name ?? '').toLowerCase().includes(q) ||
      (v.email ?? '').toLowerCase().includes(q) ||
      (v.mobile_number ?? '').includes(q) ||
      String(v.commission_percentage).includes(q)
    );
  });

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <div className="page-title">
        <div className="title_left">
          <h3>
            <i className="bi bi-percent me-2 text-success" />
            Vendor Commission Rates
          </h3>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      {updateSuccess && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle me-2" />
          {updateSuccess}
        </div>
      )}

      <div className="x_panel">
        <div className="x_title">
          <h4>
            Commission Rates
            {filtered.length !== data.length
              ? ` (${filtered.length} of ${data.length})`
              : ` (${data.length} vendors)`}
          </h4>
          <div className="clearfix" />
        </div>
        <div className="x_content">
          {/* Search */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <i className="fas fa-search text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, email, mobile…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
                {search && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => { setSearch(''); setPage(1); }}
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : filtered.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table table-striped table-hover table-bordered">
                  <thead className="table-dark">
                    <tr>
                      <th>#</th>
                      <th>Vendor Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Commission %</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((v, idx) => (
                      <tr key={v.vendor_id}>
                        <td>{(page - 1) * pageSize + idx + 1}</td>
                        <td>{v.vendor_name}</td>
                        <td>{v.email}</td>
                        <td>{v.mobile_number}</td>
                        <td>
                          <span
                            className="badge bg-success"
                            style={{ fontSize: 13, padding: '5px 10px' }}
                          >
                            {v.commission_percentage}%
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openModal(v)}
                          >
                            <i className="fas fa-edit me-1" /> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={page}
                totalItems={filtered.length}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              />
            </>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-2" />
              {search ? `No vendors match "${search}"` : 'No commission data found.'}
            </div>
          )}
        </div>
      </div>

      {/* Edit Commission Modal */}
      {modalOpen && selected && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={closeModal}
          />
          {/* Modal */}
          <div
            className="modal fade show d-block"
            style={{ zIndex: 1050 }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header" style={{ background: 'var(--gt-secondary)', color: '#fff' }}>
                  <h5 className="modal-title">
                    <i className="fas fa-edit me-2" />
                    Edit Commission — {selected.vendor_name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeModal}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  {updateError && (
                    <div className="alert alert-danger py-2">
                      <i className="fas fa-exclamation-triangle me-2" />
                      {updateError}
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Vendor</label>
                    <p className="mb-0 text-muted">{selected.vendor_name}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Current Commission</label>
                    <p className="mb-0">
                      <span className="badge bg-secondary" style={{ fontSize: 13 }}>
                        {selected.commission_percentage}%
                      </span>
                    </p>
                  </div>
                  <div className="mb-1">
                    <label className="form-label fw-semibold" htmlFor="newPct">
                      New Commission Percentage <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        id="newPct"
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        className={`form-control${percentageErr ? ' is-invalid' : ''}`}
                        value={newPercentage}
                        onChange={(e) => {
                          setNewPercentage(e.target.value);
                          setPercentageErr('');
                        }}
                        placeholder="0 – 100"
                        autoFocus
                      />
                      <span className="input-group-text">%</span>
                      {percentageErr && (
                        <div className="invalid-feedback">{percentageErr}</div>
                      )}
                    </div>
                    <small className="text-muted">
                      Vendor receives this % of the charging amount. Platform keeps the rest.
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={updating}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success px-4"
                    onClick={handleUpdate}
                    disabled={updating}
                  >
                    {updating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Updating…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2" /> Update
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VendorCommissionPage;
